import { AppConfigProvider } from '@root/app/app.config.provider';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { EquationsService } from '../equations/equations.service';
import { TrackingService } from '../tracking/tracking.service';
import { ExogenousResponse } from '@interfaces/exogenous-response';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';

@Injectable({ providedIn: 'root' })
export class ExogenousLinesService {
  private RESOURCE_ROUTE_PATH_EXO;
  private EXOGENOUS_API_URL;
  public sourceIdentifiers = [];
  public myUserId: number = null;
  public onLoadTimestamp: number = null;
  public onEditTimestamp: number = null;

  constructor(
    private http: HttpClient,
    private auth: OpenIdConnectService,
    private eqService: EquationsService,
    private tracking: TrackingService,
    private appConfig: AppConfigProvider
  ) {
    this.appConfig.environment.subscribe(env => {
      this.RESOURCE_ROUTE_PATH_EXO = '/api/exogenous';
      this.EXOGENOUS_API_URL = `${env.API.EXOGENOUSAPI}${this.RESOURCE_ROUTE_PATH_EXO}`;
    });
  }

  getExogenousLines(): Observable<any> {
    return this.http.get(this.EXOGENOUS_API_URL);
  }

  // Gets all the Exogenous Lines
  public getAllExogenousLines(): Observable<object[]> {
    // const targetUrl = environment.apiExogenous + `${RESOURCE_ROUTE_PATH_EXO}`;
    const exogenousLines$ = this.http.get<object[]>(this.EXOGENOUS_API_URL).pipe(/*retry(5)*/) || of([]);
    return exogenousLines$;
  }

  addExogenousLines(exogenousLines: ExogenousResponse[]): Observable<any> {
    const url = this.EXOGENOUS_API_URL + '/add';
    return this.http.post(url, exogenousLines);
  }

  // If some conflict occurs while merge from sprint 10 forward, choose the plural version "LineS"
  public updateExogenousLines(exogenousLines: ExogenousResponse[]): Observable<any> {
    const saveURL = this.EXOGENOUS_API_URL + '/update';
    return this.http.post(saveURL, exogenousLines);
  }

  deleteExogenousLines(exogenousLines: ExogenousResponse[]): Observable<any> {
    this.deleteExogenousLinesTrackingItem(exogenousLines);
    const url = this.EXOGENOUS_API_URL + '/delete';
    return this.http.post(url, exogenousLines);
  }

  // Bug fix 2276: Delete tracking record when the user has delete the exogenous line
  // Note: It can be done through the store procedure that handles the save, but is done
  // here to make each microservice independently handle his own domain
  // (tracking for tracking-related task, exogenous for exogenous lines period eq.)
  public deleteExogenousLinesTrackingItem(exogenousLines: ExogenousResponse[]) {
    exogenousLines.forEach(async exo => {
      const resourceId = exo['Exogenous_Sid'];
      const trackingDeleted = await this.eqService.deletePeriodEquationsTrackingItems(
        'ExogenousLinesPeriodEquations',
        resourceId,
        this.auth.userId
      );
      if (trackingDeleted) {
        // console.log('Deletion of tracking data for Exogenous Line Period Equations', resourceId, trackingDeleted);
      } else {
        // console.error(`The period equations tracking item for exogenous line ${resourceId} cannot be deleted`);
      }
    });
  }

  /*
    PERIOD EQUATIONS
  */

  public async getAllExogenousLinesPeriodEquations() {
    const sources = await this.getAllExogenousLines().toPromise();
    const listOfIds = sources.map(source => source['Exogenous_Sid']);
    const allEquations = await this.eqService.getAllPeriodEquations('ExogenousLinesPeriodEquations', listOfIds);
    const hasTemp = allEquations.some(equation => equation['isFromTracking']);
    this.tracking.updateModelDataChangesFlags('exogenousLines', hasTemp, 'getAllExogenousLinesPeriodEquations');
    return allEquations;
  }

  /**
   * Checks if exists a previous temporary record. If exists, returns an promise of it.
   * If it does not exist, calls the database to get data
   */
  public async getPeriodEquations(userId: number, exogenousLine: number): Promise<Object> {
    return await this.eqService.getPeriodEquations('ExogenousLinesPeriodEquations', userId, exogenousLine);
  }
}
